import type { files } from "@dropbox/api-v2-client/types/dropbox_types";
import { callApiV2 } from "@mirage/service-dbx-api";
import { useCallback, useEffect, useState } from "react";

// API callers + data parsers for FSS API

export function useFolderMetadata(
  folderID: string | undefined,
): files.FolderMetadata | undefined {
  const getFolderMetadata = useCallback(
    async (
      folderID: string | undefined,
    ): Promise<files.FolderMetadata | undefined> => {
      if (!folderID) {
        return undefined;
      }
      const result = await callApiV2("filesGetMetadata", {
        path: folderID,
      });
      if (result && result[".tag"] === "folder") {
        return result;
      }
      return undefined;
    },
    [],
  );
  const callResult = useApiCallResult(getFolderMetadata, folderID);
  return callResult?.type === "success" ? callResult.result : undefined;
}

export function useFolderList(
  folderID: string | undefined,
): files.ListFolderResult["entries"] | undefined {
  const getFolderList = useCallback(
    async (
      folderID: string | undefined,
    ): Promise<files.ListFolderResult["entries"] | undefined> => {
      if (!folderID) {
        return undefined;
      }
      const result = await callApiV2("filesListFolder", {
        path: folderID,
        limit: 2000, // TODO: may need to cursor this
      });
      return result.entries;
    },
    [],
  );
  const callResult = useApiCallResult(getFolderList, folderID);
  return callResult?.type === "success" ? callResult.result : undefined;
}

type CallState<T, Args> =
  | {
      type: "loading";
      args: Args;
    }
  | {
      type: "success";
      args: Args;
      result: T;
    }
  | {
      type: "error";
      args: Args;
      error: Error;
    };

function useApiCallResult<T, Args>(
  call: (args: Args) => Promise<T>,
  args: Args,
): CallState<T, Args> | undefined {
  const [callState, setCallState] = useState<CallState<T, Args> | undefined>(
    undefined,
  );

  useEffect(() => {
    setCallState({ type: "loading", args });
    call(args)
      .then((result) => {
        setCallState((prev) => {
          if (prev?.args !== args) {
            // ignore stale results
            return prev;
          }
          return { type: "success", args, result };
        });
        return;
      })
      .catch((error) => {
        setCallState((prev) => {
          if (prev?.args !== args) {
            // ignore stale results
            return prev;
          }
          return { type: "error", args, error };
        });
      });
  }, [call, args]);

  return callState;
}
